import { calculateAspectRatio } from "helpers/utility/utilities";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import RelevanceIcon from "views/uikit/RelevanceIcon";
import { useEffect, useRef, useState } from "react";
import BoundingBox from "./BoundingBox";
import { ImageRelevance } from "types";
import classNames from "classnames";

import styles from "./style.module.scss";
import { isNullOrUndefined } from "../../../../../helpers/utility/isNullOrUndefined";
import { FrameIndication } from "../../../../components/CardBodyBoxes/FrameIndication/FrameIndication";

const ObjectPreview = ({
  src,
  labels,
  boundingBoxes,
  showAssetPreview,
  relevance,
  handleMouseEnter,
  handleMouseLeave,
  timestamp,
}: ObjectPreviewProps) => {
  const [loading, setLoading] = useState(true);

  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    height: 0,
    width: 0,
  });

  const [boundingBoxContainerDimensions, setBoundingBoxContainerDimensions] =
    useState<ImageDimensions>({
      height: 0,
      width: 0,
    });

  const objectImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const showRelevance = relevance && !loading;

  const objectImageWrapperClassnames = classNames(styles.objectImageWrapper, {
    [styles.objectImageWrapperForAssetPreview]: showAssetPreview,
  });

  const calculateNewImageDimensions = () => {
    if (objectImageRef.current) {
      let newValues = calculateAspectRatio(objectImageRef.current);
      setBoundingBoxContainerDimensions({
        height: newValues[0],
        width: newValues[1],
      });
    }
  };

  const handleLoad = () => {
    setLoading(false);
    calculateNewImageDimensions();
    setImageDimensions({
      height: objectImageRef.current?.naturalHeight as number,
      width: objectImageRef.current?.naturalWidth as number,
    });
  };

  const boundingBoxContainerStyle = () => {
    if (
      boundingBoxContainerDimensions.height > 0 &&
      boundingBoxContainerDimensions.width > 0
    ) {
      return {
        height: `${boundingBoxContainerDimensions.height}px`,
        width: `${boundingBoxContainerDimensions.width}px`,
      };
    } else {
      return undefined;
    }
  };

  useEffect(() => {
    calculateNewImageDimensions();
    const objectImageElement = objectImageRef.current;
    if (!objectImageElement) return;

    const resizeObserver = new ResizeObserver(calculateNewImageDimensions);
    resizeObserver.observe(objectImageElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [objectImageRef, src]);

  return (
    <ScrollAwareHover
      ref={containerRef}
      className={objectImageWrapperClassnames}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <>
        <div
          className={styles.boundingBoxContainer}
          style={boundingBoxContainerStyle()}
        >
          {boundingBoxes.map((item: number[], index: number) => {
            const singleLabel = labels ? labels[index] || undefined : undefined;
            return (
              <BoundingBox
                key={index}
                bbValues={item}
                label={singleLabel}
                isLoading={loading}
                imageDimensions={imageDimensions}
                bbContainerDimensions={boundingBoxContainerDimensions}
              />
            );
          })}
        </div>
        {!isNullOrUndefined(timestamp) && (
          <FrameIndication timestamp={timestamp!} />
        )}
        {showRelevance && <RelevanceIcon relevance={relevance} />}

        <img
          src={src}
          ref={objectImageRef}
          data-testid="object-image"
          className={styles.objectImage}
          alt="object img"
          loading="lazy"
          onLoad={handleLoad}
        />
      </>
    </ScrollAwareHover>
  );
};

export default ObjectPreview;

interface ObjectPreviewProps {
  src: string;
  labels: string[];
  boundingBoxes: number[][];
  showAssetPreview: boolean;
  relevance?: ImageRelevance;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  timestamp?: number;
}

type ImageDimensions = { height: number; width: number };
